import {
  IssueSeverity,
  IssueType,
  PatternType,
  PromptIssue,
} from '../Services/PromptApiProviderTypes';

// ─── Types  ──────────────────────────────────────────────────

export interface IssueCardProps {
  issue: PromptIssue;
  isActive: boolean;
  isFixed: boolean;
  onClick: (id: string) => void;
  cardRef: (el: HTMLDivElement | null) => void;
}

export interface PromptAnalyzerModalProps {
  promptText: string;
  patternType?: PatternType;
  isOpen: boolean;
  onClose: () => void;
  onApply: (newPrompt: string) => void;
}
export interface HighlightRange {
  start: number;
  end: number;
  issueId: string;
  severity: IssueSeverity;
}

export interface HighlightPart {
  text: string;
  issueId: string | null;
  severity: IssueSeverity | null;
}

// --- Reducer ---
export interface AnalyzerState {
  editableText: string;
  issues: PromptIssue[];
  summary: string;
  activeIssueId: string | null;
  isEditing: boolean;
  analyzing: boolean;
  rewriting: boolean;
  hasAnalyzed: boolean;
  rewrittenText: string;
  changeSummary: string;
  error: string;
  promptTab: 'original' | 'rewritten';
  copied: boolean;
}

export type AnalyzerAction =
  | { type: 'RESET'; promptText: string }
  | { type: 'SET_EDITABLE_TEXT'; text: string }
  | { type: 'ANALYZE_START' }
  | { type: 'ANALYZE_SUCCESS'; issues: PromptIssue[]; summary: string }
  | { type: 'ANALYZE_ERROR'; error: string }
  | { type: 'REWRITE_START' }
  | { type: 'REWRITE_SUCCESS'; optimizedPrompt: string; changeSummary: string }
  | { type: 'REWRITE_ERROR'; error: string }
  | { type: 'ACCEPT_REWRITE'; rewrittenText: string }
  | { type: 'SET_ACTIVE_ISSUE'; id: string | null }
  | { type: 'SET_PROMPT_TAB'; tab: 'original' | 'rewritten' }
  | { type: 'TOGGLE_EDIT' }
  | { type: 'SET_COPIED'; copied: boolean };

// ─── Constants ──────────────────────────────────────────────────

export const SEVERITY_ORDER: Record<IssueSeverity, number> = {
  high: 0,
  medium: 1,
  low: 2,
};

export const TYPE_ICONS: Record<IssueType, string> = {
  conflict: '⚡',
  redundancy: '♻',
  ambiguity: '?',
};

export const TYPE_LABELS: Record<IssueType, string> = {
  conflict: 'Conflict',
  redundancy: 'Redundancy',
  ambiguity: 'Ambiguity',
};

// ─── Utils ──────────────────────────────────────────────────

export function buildHighlightedParts(text: string, issues: PromptIssue[]): HighlightPart[] {
  if (!text || !issues || issues.length === 0) {
    return [{ text, issueId: null, severity: null }];
  }

  const ranges: HighlightRange[] = [];
  for (const issue of issues) {
    for (const seg of issue.segments || []) {
      let startIdx = 0;
      while (startIdx < text.length) {
        const idx = text.indexOf(seg, startIdx);
        if (idx === -1) break;
        ranges.push({
          start: idx,
          end: idx + seg.length,
          issueId: issue.id,
          severity: issue.severity,
        });
        startIdx = idx + 1;
      }
    }
  }

  if (ranges.length === 0) return [{ text, issueId: null, severity: null }];

  ranges.sort((a, b) => a.start - b.start || b.end - b.start - (a.end - a.start));

  const merged: HighlightRange[] = [];
  for (const r of ranges) {
    if (merged.length > 0) {
      const last = merged[merged.length - 1];
      if (r.start < last.end) {
        last.end = Math.max(last.end, r.end);
        if (SEVERITY_ORDER[r.severity] < SEVERITY_ORDER[last.severity]) {
          last.severity = r.severity;
          last.issueId = r.issueId;
        }
        continue;
      }
    }
    merged.push({ ...r });
  }

  const parts: HighlightPart[] = [];
  let cursor = 0;
  for (const r of merged) {
    if (r.start > cursor) {
      parts.push({ text: text.slice(cursor, r.start), issueId: null, severity: null });
    }
    parts.push({
      text: text.slice(r.start, r.end),
      issueId: r.issueId,
      severity: r.severity,
    });
    cursor = r.end;
  }
  if (cursor < text.length) {
    parts.push({ text: text.slice(cursor), issueId: null, severity: null });
  }

  return parts;
}

export function scrollToElement(
  el: HTMLElement | null,
  container: HTMLElement | null,
  offset: number = 0,
): void {
  if (!el || !container) return;

  const elRect = el.getBoundingClientRect();
  const containerRect = container.getBoundingClientRect();
  const top = elRect.top - containerRect.top + container.scrollTop - offset;
  container.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
}

export function flashElement(
  el: HTMLElement | null,
  className: string,
  duration: number = 700,
): void {
  if (!el) return;
  el.classList.add(className);
  setTimeout(() => el.classList.remove(className), duration);
}

export async function copyToClipboard(text: string): Promise<void> {
  try {
    await navigator.clipboard.writeText(text);
  } catch {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    document.execCommand('copy');
    document.body.removeChild(ta);
  }
}
